import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Package,
  Search,
  FileSpreadsheet
} from "lucide-react";

const API = "/api";

export default function EnvanterRaporuPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [depolar, setDepolar] = useState([]);
  const [selectedDepo, setSelectedDepo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [rapor, setRapor] = useState(null);

  useEffect(() => {
    fetchDepolar();
  }, [token]);

  const fetchDepolar = async () => {
    try {
      const response = await axios.get(`${API}/nebim/depolar?token=${token}`);
      if (response.data.success) {
        setDepolar(response.data.depolar || []);
      }
    } catch (error) {
      console.error("Depolar alınamadı:", error);
    }
  };

  const fetchRapor = async () => {
    if (!selectedDepo) {
      toast.error("Lütfen depo seçin");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/nebim/rapor/envanter?token=${token}&depo=${selectedDepo}`
      );
      if (response.data.success) {
        setRapor(response.data.rapor);
      } else {
        toast.error(response.data.error || "Rapor alınamadı");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rapor hatası");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = rapor?.urunler?.filter(urun => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (urun.ItemCode || "").toLowerCase().includes(term) ||
      (urun.ItemDescription || "").toLowerCase().includes(term) ||
      (urun.Barcode || "").toLowerCase().includes(term)
    );
  }) || [];

  return (
    <div className="space-y-6 fade-in" data-testid="envanter-raporu-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ürün Envanter Raporu</h1>
          <p className="text-slate-500 text-sm">Depo bazlı stok durumu</p>
        </div>
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Depo</Label>
              <Select value={selectedDepo} onValueChange={setSelectedDepo}>
                <SelectTrigger className="w-[200px]" data-testid="select-depo">
                  <SelectValue placeholder="Depo Seçin" />
                </SelectTrigger>
                <SelectContent>
                  {depolar.map((depo) => (
                    <SelectItem key={depo.kod} value={depo.kod}>
                      {depo.ad} ({depo.kod})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchRapor} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
              {loading ? "Yükleniyor..." : "Rapor Getir"}
            </Button>
            <Button variant="outline">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel'e Aktar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Özet Kartları */}
      {rapor && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Package className="w-4 h-4" />
                  Ürün Çeşidi
                </div>
                <p className="text-2xl font-bold">{rapor.ozet?.toplamUrunCesidi || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Package className="w-4 h-4" />
                  Toplam Envanter
                </div>
                <p className="text-2xl font-bold">{rapor.ozet?.toplamEnvanterMiktari || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Package className="w-4 h-4" />
                  Toplam Maliyet
                </div>
                <p className="text-2xl font-bold text-emerald-600">{(rapor.ozet?.toplamMaliyetTutar || 0).toLocaleString("tr-TR")} ₺</p>
              </CardContent>
            </Card>
          </div>

          {/* Arama */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Ürün kodu, adı veya barkod ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Ürün Listesi */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Ürün Kodu</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Ürün Adı</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Envanter</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Maliyet Fiyat</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-600">Maliyet Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((urun, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="p-3 font-mono text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => window.open(`/api/nebim/urun-foto-proxy?itemCode=${urun.ItemCode}`, '_blank')}>
                          {urun.ItemCode}
                        </td>
                        <td className="p-3">{urun.ItemDescription}</td>
                        <td className="p-3 text-right font-medium">{urun.Envanter || 0}</td>
                        <td className="p-3 text-right">{(urun.MaliyetFiyat || 0).toLocaleString("tr-TR")} ₺</td>
                        <td className="p-3 text-right text-emerald-600 font-medium">{(urun.MaliyetTutar || 0).toLocaleString("tr-TR")} ₺</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredProducts.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  {searchTerm ? "Arama kriterine uygun ürün bulunamadı" : "Ürün bulunamadı"}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!rapor && !loading && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Depo seçip "Rapor Getir" butonuna tıklayın</p>
        </div>
      )}
    </div>
  );
}
